// R-NAV-6 — real 404 page (spec §4.10). Route: catch-all `*`.
//
// Before: an unknown path rendered the landing page. Now it renders this.
//
// Mounted OUTSIDE the AppShell layout (a top-level sibling of the layout
// route in router.tsx) on purpose: AppShell classifies any unknown first
// segment as WORKSPACE and would bounce an anonymous visitor to /login — so a
// bad URL would never show a 404 to a logged-out user. Rendering standalone
// (still inside Theme/Auth/Role providers + the Router) lets BOTH anon and
// authed visitors see a proper 404 with a way home. Self-contained chrome
// (no Nav/Footer) keeps it independent of the auth gate.
//
// R2-004 — the audit found this page monolingual: an Arabic-reading visitor hit
// four Persian lines on every wrong URL. Every string now comes from `common`,
// which is a BOOT namespace — this page renders outside the shell, so it cannot
// wait for a lazily loaded namespace. The «۴۰۴» itself goes through the shared
// number formatter, else Arabic would show Persian-Indic digits.
import React from "react";
import { useTranslation } from "react-i18next";

import { formatNumberFa } from "../i18n/format.js";
import { Button } from "../ui";
import { useAuth } from "../auth/AuthContext";
import { useRole } from "../role";
import { useGo } from "../router";

const NotFoundPage = (): React.ReactElement => {
  const { t } = useTranslation("common");
  const go = useGo();
  const { isAuthenticated } = useAuth();
  const { role, activeRoleId } = useRole();
  // Authed → back to the role's home; anon → the public landing.
  const homeRoute = isAuthenticated && activeRoleId ? role.homeRoute : "home";
  // >1 means this session navigated here from inside the app; 1 (or 0 in older
  // engines) means the 404 IS the entry point and there is nothing to go back to.
  const canGoBack = typeof window !== "undefined" && window.history.length > 1;

  return (
    <main
      id="not-found"
      data-screen-label={t("notFound.screenLabel")}
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "var(--bg)",
        color: "var(--fg)",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 440 }}>
        <div
          aria-hidden="true"
          style={{ fontSize: 72, fontWeight: 800, lineHeight: 1, color: "var(--accent)", fontFamily: "var(--f-mono)" }}
        >
          {formatNumberFa(404)}
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginTop: 16 }}>{t("notFound.title")}</h1>
        <p style={{ fontSize: 14, color: "var(--fg-mute)", lineHeight: 1.7, marginTop: 10 }}>
          {t("notFound.body")}
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 24, flexWrap: "wrap" }}>
          {/* R2-004 — «بازگشت» in the literal sense: back to where the visitor
              actually came from. Only offered when this app put an entry in the
              history stack, so it can never dead-end on the browser's own start
              page or bounce the visitor out of the site. */}
          {canGoBack ? (
            <Button variant="primary" size="md" onClick={() => window.history.back()}>
              {t("notFound.back")}
            </Button>
          ) : null}
          <Button variant={canGoBack ? "outline" : "primary"} size="md" onClick={() => go(homeRoute)}>
            {t("notFound.home")}
          </Button>
          {isAuthenticated ? (
            <Button variant="outline" size="md" onClick={() => go("search")}>
              {t("notFound.search")}
            </Button>
          ) : (
            <Button variant="outline" size="md" onClick={() => go("programs")}>
              {t("notFound.programs")}
            </Button>
          )}
        </div>
      </div>
    </main>
  );
};

export default NotFoundPage;
